import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageCircle, Facebook, Send, Users, ExternalLink, Star, MapPin } from "lucide-react";
import api from "@/lib/api";
import { SloganQuote } from "@/components/SloganQuote";

const platformIcon = { whatsapp: MessageCircle, facebook: Facebook, telegram: Send, other: Users };

export default function Community() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/community-groups").then((r) => setGroups(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-slate-950 text-white min-h-screen" data-testid="community-page">
      <div className="max-w-5xl mx-auto px-6 py-14">
        <span className="text-xs uppercase tracking-[0.3em] font-bold text-orange-500">OXO Hub</span>
        <h1 className="text-3xl sm:text-5xl font-black tracking-tighter mt-3">{t("community.title")}</h1>
        <p className="text-slate-400 mt-4 max-w-2xl">{t("community.subtitle")}</p>
        <SloganQuote sloganKey="slogan_craft" dark testid="community-slogan" className="mt-6 max-w-2xl text-base sm:text-lg" />

        {loading ? (
          <div className="py-16 text-center text-slate-500">{t("common.loading")}</div>
        ) : groups.length === 0 ? (
          <div className="border border-slate-800 p-12 text-center text-slate-500 mt-10" data-testid="community-empty">{t("community.empty")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            {groups.map((g) => {
              const Icon = platformIcon[g.platform] || Users;
              return (
                <a key={g.id} href={g.link} target="_blank" rel="noreferrer"
                   className={`group border p-5 transition-colors ${g.promoted ? "border-orange-500/60 bg-orange-950/20" : "border-slate-800 bg-slate-900/40 hover:border-slate-700"}`}
                   data-testid={`community-group-${g.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 bg-slate-800 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-orange-500" /></span>
                      <div>
                        <div className="font-bold leading-tight">{g.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 capitalize">{g.platform}{g.region && <><span>·</span><MapPin className="w-3 h-3" />{g.region}</>}</div>
                      </div>
                    </div>
                    {g.promoted && <span className="inline-flex items-center gap-1 bg-orange-500 text-slate-900 text-[10px] font-bold uppercase tracking-wide px-2 py-1 shrink-0" data-testid="community-promoted"><Star className="w-3 h-3" /> {t("community.promoted")}</span>}
                  </div>
                  {g.description && <p className="text-sm text-slate-400 mt-3">{g.description}</p>}
                  <span className="inline-flex items-center gap-2 text-sm font-bold text-orange-500 mt-4 group-hover:gap-3 transition-all">{t("community.join")} <ExternalLink className="w-3.5 h-3.5" /></span>
                </a>
              );
            })}
          </div>
        )}
        <p className="text-xs text-slate-600 mt-10">{t("community.disclaimer")}</p>

        <section className="mt-20 pt-14 border-t border-slate-800" data-testid="community-history" id="istoria">
          <span className="text-xs uppercase tracking-[0.3em] font-bold text-orange-500">{t("community.history.eyebrow")}</span>
          <h2 className="text-2xl sm:text-4xl font-black tracking-tighter mt-3 max-w-3xl">{t("community.history.title")}</h2>
          <div className="grid lg:grid-cols-2 gap-10 mt-8 items-start">
            <div className="space-y-4 text-slate-300 leading-relaxed">
              <p data-testid="history-p1">{t("community.history.p1")}</p>
              <p data-testid="history-p2">{t("community.history.p2")}</p>
              <p data-testid="history-p3">{t("community.history.p3")}</p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1684183819704-ec0da9a49dca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                alt={t("community.history.title")}
                className="w-full h-72 sm:h-80 object-cover grayscale border border-slate-800"
                loading="lazy"
              />
              <span className="absolute bottom-0 left-0 bg-orange-500 text-slate-900 text-xs font-bold uppercase tracking-wide px-3 py-2" data-testid="history-highlight">
                {t("community.history.highlight")}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10" data-testid="history-stats">
            <div className="border border-slate-800 bg-slate-900/40 p-6">
              <div className="text-3xl font-black text-orange-500">{t("community.history.stat_year")}</div>
              <div className="text-xs uppercase tracking-wide text-slate-400 mt-1">{t("community.history.stat_year_label")}</div>
            </div>
            <div className="border border-slate-800 bg-slate-900/40 p-6">
              <div className="text-3xl font-black text-orange-500">{t("community.history.stat_expo")}</div>
              <div className="text-xs uppercase tracking-wide text-slate-400 mt-1">{t("community.history.stat_expo_label")}</div>
            </div>
            <div className="border border-slate-800 bg-slate-900/40 p-6">
              <div className="text-xl font-black text-orange-500">{t("community.history.stat_east")}</div>
              <div className="text-xs uppercase tracking-wide text-slate-400 mt-1">{t("community.history.stat_east_label")}</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
